const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toDisplay = (value, fallback = "") => {
  const trimmed = String(value ?? "").trim();
  return trimmed ? escapeHtml(trimmed) : fallback;
};

const toMultilineHtml = (value, fallback = "") => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return fallback;
  return escapeHtml(trimmed).replace(/\n/g, "<br/>");
};

const isTruthy = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value ?? "").toLowerCase() === "true";

const toYesNo = (value) => (isTruthy(value) ? "Yes" : "No");

const formatDate = (value) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return toDisplay(value);
  return parsedDate.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatPesos = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const numeric = Number(String(trimmed).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(numeric)) return escapeHtml(trimmed);
  return `&#8369; ${numeric.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatPercent = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const numeric = Number(String(trimmed).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(numeric)) return escapeHtml(trimmed);
  return `${numeric.toFixed(2)}%`;
};

const providerTypeLabel = (value) => {
  if (value === "h") return "Hospital";
  if (value === "c") return "Clinic";
  return "";
};

const getPaymentModeLabel = (value, includeAutoCredit = true) => {
  const map = includeAutoCredit
    ? { "1": "Bank Deposit", "2": "Check Pick-up", "3": "Check Delivery", "4": "Auto Credit" }
    : { "1": "Bank Deposit", "2": "Check Pick-up", "3": "Check Delivery" };
  return map[String(value ?? "")] || "";
};

const ipTermsText = (formData) => {
  const parts = [];
  const whole = getPaymentModeLabel(formData?.ip_whole_setup, true);
  const splitHosp = getPaymentModeLabel(formData?.ip_split_hospital, true);
  const splitDoc = getPaymentModeLabel(formData?.ip_split_doctor, false);
  if (whole) parts.push(`Whole (${whole})`);
  if (splitHosp) parts.push(`Split - HB to Hospital (${splitHosp})`);
  if (splitDoc) parts.push(`Split - PF to Doctor (${splitDoc})`);
  return parts.length ? parts.join(", ") : "&mdash;";
};

const emergencyText = (formData) => {
  const mode = getPaymentModeLabel(formData?.emergency_whole_setup, true);
  return mode ? `Paid to Hospital (${mode})` : "&mdash;";
};

const opConsultText = (formData) => {
  const parts = [];
  const hosp = getPaymentModeLabel(formData?.op_hospital_setup, true);
  const doc = getPaymentModeLabel(formData?.op_doctor_setup, false);
  if (hosp) parts.push(`Paid to Hospital (${hosp})`);
  if (doc) parts.push(`Paid to Doctor (${doc})`);
  return parts.length ? parts.join(", ") : "&mdash;";
};

const labText = (formData) => {
  const mode = getPaymentModeLabel(formData?.lab_hospital_setup, true);
  return mode ? `Paid to Hospital (${mode})` : "&mdash;";
};

const clinicText = (formData) => {
  const parts = [];
  const hosp = getPaymentModeLabel(formData?.clinic_hospital_setup, true);
  const doc = getPaymentModeLabel(formData?.clinic_doctor_setup, false);
  if (hosp) parts.push(`Paid to Hospital (${hosp})`);
  if (doc) parts.push(`Paid to Doctor (${doc})`);
  return parts.length ? parts.join(", ") : "&mdash;";
};

const getInternetText = (formData) => {
  const entries = [];
  if (isTruthy(formData?.internet_industrial_dept)) entries.push("Industrial Dept.");
  if (isTruthy(formData?.internet_billing_dept)) entries.push("Billing Dept.");
  if (isTruthy(formData?.internet_emergency)) entries.push("Emergency");
  return entries.length ? entries.join(", ") : "";
};

const getWifiText = (formData) => {
  const entries = [];
  if (isTruthy(formData?.wifi_industrial_hmo)) entries.push("Industrial/HMO Dept.");
  if (isTruthy(formData?.wifi_ip_rooms)) entries.push("Hospital In-Patient Rooms");
  if (isTruthy(formData?.wifi_emergency)) entries.push("Emergency");
  if (isTruthy(formData?.wifi_mab)) entries.push("MAB");
  return entries.length ? entries.join(", ") : "";
};

const getProviderAddressText = (formData) => {
  const values = [
    formData?.streetno_bldg,
    formData?.barangay,
    formData?.towncity,
    formData?.province,
    formData?.postalcode,
    formData?.region,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  return values.length ? values.join(", ") : "";
};

const defaultContactDepartments = [
  { key: 0, name: "HMO/INDUSTRIAL DEPARTMENT", designation: "HMO/Industrial Department" },
  { key: 1, name: "ADMITTING DEPARTMENT", designation: "Admitting Department" },
  { key: 2, name: "EMERGENCY DEPARTMENT", designation: "Emergency Department" },
  { key: 3, name: "BILLING DEPARTMENT", designation: "Billing Department" },
  { key: 4, name: "CREDIT AND COLLECTION DEPARTMENT", designation: "Credit & Collection Department" },
];

const getContactForDepartment = (formData, index, fallbackDesignation) => {
  const rows = Array.isArray(formData?.contact_details) ? formData.contact_details : [];
  const contact = rows[index] || {};
  return {
    contact_person: contact.contact_person,
    designation: contact.designation || fallbackDesignation,
    mobile_no: contact.mobile_no,
    email_add: contact.email_add,
    fax_no: contact.fax_no,
    landline_no: contact.landline_no,
  };
};

const fieldValue = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed ? escapeHtml(trimmed) : "&nbsp;";
};

const departmentBlockHtml = (formData, dept) => {
  const data = getContactForDepartment(formData, dept.key, dept.designation);
  return `
    <div class="row dept-block">
      <h5 class="dept-title">${dept.name}</h5>
      <div class="col col-xs-6">
        <div class="row pair"><div class="col col-xs-4">a. Contact Person:</div><div class="col col-xs-8 border-bottom">${fieldValue(data.contact_person)}</div></div>
        <div class="row pair"><div class="col col-xs-4">c. Landline Nos.:</div><div class="col col-xs-8 border-bottom">${fieldValue(data.landline_no)}</div></div>
        <div class="row pair"><div class="col col-xs-4">e. Email Address:</div><div class="col col-xs-8 border-bottom">${fieldValue(data.email_add)}</div></div>
      </div>
      <div class="col col-xs-6">
        <div class="row pair"><div class="col col-xs-4">b. Designation:</div><div class="col col-xs-8 border-bottom">${fieldValue(data.designation)}</div></div>
        <div class="row pair"><div class="col col-xs-4">d. Mobile Nos.:</div><div class="col col-xs-8 border-bottom">${fieldValue(data.mobile_no)}</div></div>
        <div class="row pair"><div class="col col-xs-4">f. Fax Nos.:</div><div class="col col-xs-8 border-bottom">${fieldValue(data.fax_no)}</div></div>
      </div>
    </div>
  `;
};

const resolveLogoUrl = () => {
  if (typeof window === "undefined") return "/amaphil-logo-350.png";
  const origin = window.location?.origin || "";
  return `${origin}/amaphil-logo-350.png`;
};

const buildProviderPrintHtml = ({ formData, providerCode }) => {
  const generationDate = new Date().toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const logoUrl = resolveLogoUrl();

  const completeAddress = getProviderAddressText(formData);
  const region = String(formData?.region ?? "").trim();
  const provType = providerTypeLabel(formData?.provider_type);

  const departmentsHtml = defaultContactDepartments
    .map((dept) => departmentBlockHtml(formData, dept))
    .join("");

  const taxExempt = toYesNo(formData?.tax_exempt_tag);
  const vatRegistered = toYesNo(formData?.vat_registered);
  const promptDiscount = toYesNo(formData?.ppd_tag);
  const mcFlag = toYesNo(formData?.mc_flag);
  const ppdRate = formatPercent(formData?.ppd_rate);

  const bankName = [formData?.bank_id, formData?.bank_branch]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(", ");

  const form2303FileName =
    formData?.document_file_name ||
    formData?.bank_proof_file_name ||
    "";

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Provider Enrollment Form - ${toDisplay(
    formData?.provider_name || providerCode || "Provider"
  )}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Open Sans", "Segoe UI", Arial, sans-serif;
      background: #eef1f5;
      color: #4e5a64;
      line-height: 1.45;
      padding: 24px 16px;
      font-size: 13px;
    }
    .sheet {
      max-width: 1100px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #e7eaf0;
      box-shadow: 0 18px 40px rgba(2, 6, 23, 0.08);
      border-radius: 4px;
    }
    .letter-pad { padding: 20px; }

    /* Bootstrap-like 12-col grid */
    .row {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      column-gap: 10px;
      row-gap: 4px;
      align-items: start;
    }
    .row + .row { margin-top: 4px; }
    .row > h4, .row > h5, .row > p, .row > ul, .row > div.full {
      grid-column: 1 / -1;
    }
    .col-xs-1  { grid-column: span 1; }
    .col-xs-2  { grid-column: span 2; }
    .col-xs-3  { grid-column: span 3; }
    .col-xs-4  { grid-column: span 4; }
    .col-xs-5  { grid-column: span 5; }
    .col-xs-6  { grid-column: span 6; }
    .col-xs-7  { grid-column: span 7; }
    .col-xs-8  { grid-column: span 8; }
    .col-xs-9  { grid-column: span 9; }
    .col-xs-10 { grid-column: span 10; }
    .col-xs-11 { grid-column: span 11; }
    .col-xs-12 { grid-column: span 12; }

    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .bold { font-weight: 700; }
    .uppercase { text-transform: uppercase; }
    .underline { text-decoration: underline; }
    .font-white { color: #fff !important; }
    .control-label { color: #4e5a64; font-weight: 600; }
    .control-label.bold { color: #2f3941; font-weight: 700; }

    /* Header */
    .invoice-head {
      display: grid;
      grid-template-columns: 7fr 5fr;
      gap: 18px;
      margin-bottom: 6px;
    }
    .invoice-logo img {
      max-width: 60%;
      height: auto;
      display: block;
    }
    .company-address {
      text-align: right;
      font-size: 12px;
      color: #4e5a64;
      line-height: 1.55;
    }
    .company-address .bold { color: #2f3941; }

    /* Green title bar */
    h4.invoice-logo {
      background-color: #26c281;
      color: #fff;
      text-align: center;
      padding: 8px;
      margin: 10px 0 16px;
      font-size: 15px;
      letter-spacing: .04em;
      border-radius: 2px;
    }

    /* Field-row labels and value bottom borders */
    .border-bottom {
      border-bottom: 1px solid #4e5a64;
      min-height: 18px;
      padding: 2px 4px;
    }
    .border-bottom .control-label,
    .border-bottom label { color: #2f3941; font-weight: 600; }

    /* Section header bars */
    .print-sub-header-custom {
      padding: 6px 10px;
      border: 2px solid #26c281;
      text-align: center;
      font-style: italic;
      font-weight: 700;
      color: #26c281;
      letter-spacing: .04em;
      text-transform: uppercase;
      margin: 14px 0 10px;
      background: #fff;
    }
    .margin-bottom-10 { margin-bottom: 10px; }
    .margin-bottom-5  { margin-bottom: 5px; }

    /* Department block */
    .dept-block { margin-top: 8px; row-gap: 6px; }
    .dept-title {
      grid-column: 1 / -1;
      font-size: 13px;
      font-weight: 700;
      text-decoration: underline;
      color: #2f3941;
      margin: 4px 0 6px;
    }
    .pair { row-gap: 0; }
    .pair .col-xs-4 { padding-top: 2px; }

    /* Payment setup boxes (dv-border-solid + green title) */
    .dv-border-solid {
      border: 1px solid #2f3941;
      margin-bottom: 10px;
      border-radius: 2px;
      overflow: hidden;
    }
    .dv-border-solid .box-title {
      background-color: #26c281;
      color: #fff;
      font-weight: 700;
      padding: 6px;
      text-align: center;
      letter-spacing: .03em;
    }
    .dv-border-solid ul {
      list-style: none;
      padding: 8px 14px;
      margin: 0;
    }
    .dv-border-solid ul li { font-size: 12px; }

    /* Professional fees section */
    .prof-fees-block .pf-row {
      display: grid;
      grid-template-columns: 2fr 4fr 2fr 4fr;
      column-gap: 8px;
      row-gap: 4px;
      align-items: end;
      margin-bottom: 6px;
    }
    .prof-fees-block .pf-row.full {
      grid-template-columns: 2fr 10fr;
    }
    .prof-fees-block .pf-row .border-bottom { font-weight: 700; color: #2f3941; }

    .pf-rates {
      display: grid;
      grid-template-columns: 8fr 4fr;
      gap: 14px;
      margin-top: 6px;
    }
    .pf-rates .pf-rates-title {
      text-align: center;
      font-weight: 700;
      color: #2f3941;
      margin-bottom: 6px;
      text-transform: uppercase;
      font-size: 12px;
    }
    .pf-rates .ip-rates {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
    }
    .pf-rates .ip-rates .col,
    .pf-rates .op-rates {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .pf-rates .rate-row {
      display: grid;
      grid-template-columns: 6fr 6fr;
      column-gap: 6px;
      align-items: center;
    }
    .pf-rates .rate-row label:first-child {
      font-weight: 600;
      color: #4e5a64;
    }
    .pf-rates .rate-row .border-bottom {
      text-align: right;
      font-weight: 700;
      color: #2f3941;
    }

    /* Closing + print button */
    .print-actions { margin-top: 24px; text-align: left; }
    .print-actions .print-btn {
      display: inline-block;
      background: #26c281;
      color: #fff !important;
      padding: 12px 28px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .04em;
      cursor: pointer;
      border: 0;
      text-decoration: none;
      box-shadow: 0 1px 0 rgba(0,0,0,0.08);
    }
    .print-actions .print-btn:hover { background: #1fa86f; }

    @media print {
      body { background: #fff; padding: 0; font-size: 12px; }
      .sheet { border: 0; box-shadow: none; border-radius: 0; max-width: none; }
      .print-actions, .hidden-print { display: none !important; }
      .letter-pad { padding: 12mm 10mm; }
      .invoice-head, .dept-block, .dv-border-solid, .prof-fees-block, .pf-rates { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="letter-pad">
      <div class="invoice-head">
        <div class="invoice-logo">
          <img src="${escapeHtml(logoUrl)}" alt="AMAPHIL Logo" onerror="this.style.display='none'" />
        </div>
        <div class="company-address">
          <span class="bold uppercase">Advanced Medical Access Philippines, Inc.</span><br/>
          820 JP Rizal Street<br/>
          Barangay Poblacion, Makati City<br/>
          <span class="bold">T</span> (02) 8241-7679 | (02) 8355-7500<br/>
          <span class="bold">E</span> info@amaphil.com.ph<br/>
          <span class="bold">W</span> www.amaphil.com.ph<br/>
          <span>Date:</span> <span class="bold">${generationDate}</span>
        </div>
      </div>

      <h4 class="invoice-logo bold font-white">Provider Enrollment Form</h4>

      <div class="row">
        <div class="col-xs-3"><strong>Registered Provider Name:</strong></div>
        <div class="col-xs-9 border-bottom"><span class="control-label">${fieldValue(formData?.provider_name)}</span></div>
      </div>
      <div class="row">
        <div class="col-xs-3"><strong>Complete Address:</strong></div>
        <div class="col-xs-9 border-bottom"><span class="control-label">${fieldValue(completeAddress)}</span></div>
      </div>
      <div class="row">
        <div class="col-xs-3"><strong>Region:</strong></div>
        <div class="col-xs-4 border-bottom"><span class="control-label">${fieldValue(region)}</span></div>
        <div class="col-xs-2 text-right"><strong>Provider Type:</strong></div>
        <div class="col-xs-3 border-bottom"><span class="control-label">${fieldValue(provType)}</span></div>
      </div>

      <div class="print-sub-header-custom border-green-jungle margin-bottom-10 bold">Contact Details</div>
      ${departmentsHtml}

      <div class="print-sub-header-custom margin-bottom-5 bold">Signatories</div>
      <div class="row dept-block">
        <h5 class="dept-title">SIGNATORIES</h5>
        <div class="col-xs-6">
          <div class="row pair"><div class="col-xs-4">Name:</div><div class="col-xs-8 border-bottom">${fieldValue(formData?.signatories1)}</div></div>
          <div class="row pair"><div class="col-xs-4">Name:</div><div class="col-xs-8 border-bottom">${fieldValue(formData?.signatories2)}</div></div>
        </div>
        <div class="col-xs-6">
          <div class="row pair"><div class="col-xs-4">Designation:</div><div class="col-xs-8 border-bottom">${fieldValue(formData?.designation1)}</div></div>
          <div class="row pair"><div class="col-xs-4">Designation:</div><div class="col-xs-8 border-bottom">${fieldValue(formData?.designation2)}</div></div>
        </div>
      </div>

      <div class="print-sub-header-custom margin-bottom-5 bold">Payment Terms</div>

      <div class="row">
        <div class="col-xs-2"><span class="bold">PROVIDER TIN:</span></div>
        <div class="col-xs-2 border-bottom uppercase"><span class="control-label bold uppercase">${fieldValue(formData?.tin)}</span></div>
        <div class="col-xs-2">Tax Exempt?</div>
        <div class="col-xs-2 border-bottom">${fieldValue(taxExempt)}</div>
        <div class="col-xs-2">VAT Registered?</div>
        <div class="col-xs-2 border-bottom">${fieldValue(vatRegistered)}</div>
      </div>
      <div class="row">
        <div class="col-xs-3"><span class="bold">FORM 2303:</span></div>
        <div class="col-xs-9 border-bottom">${fieldValue(form2303FileName)}</div>
      </div>

      <h5 class="dept-title" style="margin-top:12px;">BANK DETAILS:</h5>
      <div class="row">
        <div class="col-xs-4">a. Corporate Account Name:</div>
        <div class="col-xs-8 border-bottom">${fieldValue(formData?.bank_acct_name)}</div>
      </div>
      <div class="row">
        <div class="col-xs-3">b. Account Number:</div>
        <div class="col-xs-2 border-bottom">${fieldValue(formData?.bank_acct_no)}</div>
        <div class="col-xs-3 text-right">Bank Name &amp; Branch:</div>
        <div class="col-xs-4 border-bottom">${fieldValue(bankName)}</div>
      </div>

      <div class="row" style="margin-top:8px;">
        <div class="col-xs-3"><span class="bold underline">PAYMENT TERMS:</span></div>
        <div class="col-xs-3 border-bottom">${fieldValue(
          formData?.payment_terms ? `${formData.payment_terms} Days` : ""
        )}</div>
      </div>
      <div class="row">
        <div class="col-xs-5"><span class="bold underline">SUBMISSION OF CLAIMS based on Stale Computation:</span></div>
        <div class="col-xs-3 border-bottom">${fieldValue(
          formData?.claims_submission ? `${formData.claims_submission} Days` : ""
        )}</div>
      </div>
      <div class="row">
        <div class="col-xs-5"><span class="bold underline">SUBMISSION OF CLAIMS based on Provider Contract:</span></div>
        <div class="col-xs-3 border-bottom">${fieldValue(
          formData?.claims_submission2 ? `${formData.claims_submission2} Days` : ""
        )}</div>
      </div>

      <div class="row" style="margin-top:8px;">
        <div class="col-xs-3"><span class="bold underline">HOSPITAL SET-UP:</span></div>
        <div class="col-xs-9 border-bottom">&nbsp;</div>
      </div>
      <div class="row">
        <div class="col-xs-4"><span class="bold underline">With Prompt Payment Discount?:</span></div>
        <div class="col-xs-2 border-bottom">${fieldValue(promptDiscount)}</div>
      </div>
      <div class="row">
        <div class="col-xs-4"><span class="bold underline">PPD Rate:</span></div>
        <div class="col-xs-2 border-bottom">${fieldValue(ppdRate)}</div>
      </div>

      <div class="row" style="margin-top:10px;">
        <div class="col-xs-7" id="col-ip-opd-or">
          <div class="dv-border-solid">
            <div class="box-title">IN-PATIENT / OPD-OR</div>
            <ul><li><span class="control-label">${ipTermsText(formData)}</span></li></ul>
          </div>
          <div class="dv-border-solid">
            <div class="box-title">EMERGENCY</div>
            <ul><li><span class="control-label">${emergencyText(formData)}</span></li></ul>
          </div>
        </div>
        <div class="col-xs-5">
          <div class="dv-border-solid">
            <div class="box-title">OUT PATIENT - CONSULT / REFERRAL</div>
            <ul><li><span class="control-label">${opConsultText(formData)}</span></li></ul>
          </div>
          <div class="dv-border-solid">
            <div class="box-title">OUT PATIENT - LAB / APE / FACILITY FEE</div>
            <ul><li><span class="control-label">${labText(formData)}</span></li></ul>
          </div>
          <div class="dv-border-solid">
            <div class="box-title">CLINIC SETTING</div>
            <ul><li><span class="control-label">${clinicText(formData)}</span></li></ul>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-6">
          <div class="bold" style="margin-bottom:4px;">If Check Delivery, please specify the Contact Person and Department:</div>
          <div class="border-bottom">
            ${fieldValue(formData?.checkdel_contperson)}<span style="margin:0 6px;">,</span>
            ${fieldValue(formData?.checkdel_dep)}<span style="margin:0 6px;">,</span>
            ${fieldValue(formData?.checkdel_contno)}
          </div>
        </div>
        <div class="col-xs-6" style="padding-top:18px;">
          <span class="bold">With Prompt Payment Discount?:</span>
          <span class="underline" style="margin-left:6px;">${fieldValue(promptDiscount)}</span>
        </div>
      </div>

      <div class="prof-fees-block" style="margin-top:16px;">
        <div class="bold" style="margin-bottom:6px;">1. PROFESSIONAL FEES:</div>
        <div class="pf-row full">
          <div>a. With Medical Coordinator or (MC)?</div>
          <div>
            <span class="control-label bold">${mcFlag}</span>
            &nbsp;&nbsp; MCIP Fee:
            <span class="control-label border-bottom bold" style="display:inline-block; min-width:120px; text-align:right;">${formatPesos(formData?.mc_ip_fee) || "&nbsp;"}</span>
            &nbsp;&nbsp; MCOP Fee:
            <span class="control-label border-bottom bold" style="display:inline-block; min-width:120px; text-align:right;">${formatPesos(formData?.mc_op_fee) || "&nbsp;"}</span>
          </div>
        </div>

        <div class="pf-row">
          <div>Name of Doctor:</div>
          <div class="border-bottom">${fieldValue(formData?.mc_doctor_name)}</div>
          <div class="text-right">Specialization:</div>
          <div class="border-bottom">${fieldValue(formData?.mc_specialization)}</div>
        </div>
        <div class="pf-row">
          <div>Doctor&rsquo;s Mobile No:</div>
          <div class="border-bottom">${fieldValue(formData?.mc_contact_no)}</div>
          <div class="text-right">Room Number:</div>
          <div class="border-bottom">${fieldValue(formData?.mc_room_no)}</div>
        </div>
        <div class="pf-row full">
          <div>Clinic Hours:</div>
          <div class="border-bottom">${fieldValue(formData?.mc_clinic_hours)}</div>
        </div>
        <div class="pf-row">
          <div>Name of Secretary:</div>
          <div class="border-bottom">${fieldValue(formData?.mc_secretary_name)}</div>
          <div></div><div></div>
        </div>

        <div class="bold" style="margin:10px 0 4px;">PF Rates:</div>
        <div class="pf-rates">
          <div class="ip-rates-wrap">
            <div class="pf-rates-title">In-Patient PF Rates:</div>
            <div class="ip-rates">
              <div class="col">
                <div class="rate-row"><label>Ward:</label><label class="border-bottom">${formatPesos(formData?.ip_ward_rate) || "&nbsp;"}</label></div>
                <div class="rate-row"><label>Semi-Private:</label><label class="border-bottom">${formatPesos(formData?.ip_semi_private_rate) || "&nbsp;"}</label></div>
                <div class="rate-row"><label>Private:</label><label class="border-bottom">${formatPesos(formData?.ip_private_rate) || "&nbsp;"}</label></div>
              </div>
              <div class="col">
                <div class="rate-row"><label>Suite:</label><label class="border-bottom">${formatPesos(formData?.ip_suite_rate) || "&nbsp;"}</label></div>
                <div class="rate-row"><label>ICU:</label><label class="border-bottom">${formatPesos(formData?.ip_icu_rate) || "&nbsp;"}</label></div>
                <div class="rate-row"><label>Anes Fee:</label><label class="border-bottom">${formatPercent(formData?.ip_anes_rate) || "&nbsp;"}</label></div>
              </div>
            </div>
          </div>
          <div class="op-rates-wrap">
            <div class="pf-rates-title">Out-Patient Rates:</div>
            <div class="op-rates">
              <div class="rate-row"><label>PCP:</label><label class="border-bottom">${formatPesos(formData?.op_pcp_rate) || "&nbsp;"}</label></div>
              <div class="rate-row"><label>Specialist:</label><label class="border-bottom">${formatPesos(formData?.op_specialist_rate) || "&nbsp;"}</label></div>
            </div>
          </div>
        </div>
      </div>

      <div class="print-sub-header-custom margin-bottom-5 bold" style="margin-top:16px;">Internet Connectivity</div>
      <ul style="list-style:none; padding:0; margin:0;">
        <li style="margin-bottom:4px;"><b>Internet Access:</b> <span class="control-label">${fieldValue(getInternetText(formData))}</span></li>
        <li><b>Areas with Free WiFi:</b> <span class="control-label">${fieldValue(getWifiText(formData))}</span></li>
      </ul>

      <div class="print-actions hidden-print">
        <button type="button" class="print-btn" onclick="window.print()">Print</button>
      </div>
    </div>
  </div>
</body>
</html>`;
};

export const openProviderPrintPreview = ({
  formData,
  providerCode,
  getContactDepartmentLabel,
}) => {
  if (typeof window === "undefined") return false;

  const printWindow = window.open("", "_blank", "width=1200,height=900");
  if (!printWindow) return false;

  const html = buildProviderPrintHtml({
    formData: formData || {},
    providerCode: providerCode || "",
    getContactDepartmentLabel,
  });

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  return true;
};
